import { useState, useMemo, useEffect, useRef } from "react"
import { motion } from "motion/react"
import { AnimatedNumber } from "@/components/mortgage/AnimatedNumber"
import { useDebounce } from "@/MyCode/hooks/useDebounce"
import JsonFormSteps from "@/MyCode/components/JsonFormSteps"
import type { Step } from "@/MyCode/core/types"
import debounce from "lodash.debounce"

export function MonthBudgetBasedHouseAffordability() {
    const [monthlyBudget, setMonthlyBudget] = useState(3500)
    const [loanTerm, setLoanTerm] = useState(30)
    const [interestRate, setInterestRate] = useState(6.317)
    const [downPayment, setDownPayment] = useState(20)
    const [downPaymentMode, setDownPaymentMode] = useState("percentage")
    const [includeTaxesAndFees, setIncludeTaxesAndFees] = useState(true)
    const [propertyTax, setPropertyTax] = useState(1.5)
    const [propertyTaxMode, setPropertyTaxMode] = useState("percentage")
    const [hoaFee, setHoaFee] = useState(0)
    const [hoaFeeMode, setHoaFeeMode] = useState("percentage")
    const [insurance, setInsurance] = useState(0.5)
    const [insuranceMode, setInsuranceMode] = useState("percentage")
    const [maintenanceCost, setMaintenanceCost] = useState(1.5)
    const [maintenanceCostMode, setMaintenanceCostMode] = useState("percentage")

    // Debounced values
    const debouncedMonthlyBudget = useDebounce(monthlyBudget, 500)
    const debouncedLoanTerm = useDebounce(loanTerm, 500)
    const debouncedInterestRate = useDebounce(interestRate, 500)
    const debouncedDownPayment = useDebounce(downPayment, 500)
    const debouncedPropertyTax = useDebounce(propertyTax, 500)
    const debouncedHoaFee = useDebounce(hoaFee, 500)
    const debouncedInsurance = useDebounce(insurance, 500)
    const debouncedMaintenanceCost = useDebounce(maintenanceCost, 500)

    // Calculate maximum affordable house price based on monthly budget
    // This is an iterative calculation because:
    // - House price affects property tax, HOA, insurance, maintenance (if percentage-based)
    // - House price affects down payment (if percentage-based)
    // - House price affects loan amount
    // - Loan amount affects monthly mortgage payment
    // - Total monthly payment must fit within the budget
    const affordabilityCalculation = useMemo(() => {
        if (debouncedMonthlyBudget <= 0 || debouncedInterestRate < 0 || debouncedLoanTerm <= 0) {
            return {
                maxHousePrice: 0,
                maxLoanAmount: 0,
                downPaymentAmount: 0,
                monthlyMortgagePayment: 0,
                monthlyPropertyTax: 0,
                monthlyHoaFee: 0,
                monthlyInsurance: 0,
                monthlyMaintenanceCost: 0,
                totalMonthlyPayment: 0,
                estimatedClosingCost: 0,
                totalOneTimePayment: 0,
                annualPropertyTax: 0,
                annualHoaFee: 0,
                annualInsurance: 0,
                annualMaintenanceCost: 0,
            }
        }

        // Use binary search to find maximum affordable house price
        let low = 0
        let high = debouncedMonthlyBudget * 1000 // Reasonable upper bound
        let bestResult = {
            maxHousePrice: 0,
            maxLoanAmount: 0,
            downPaymentAmount: 0,
            monthlyMortgagePayment: 0,
            monthlyPropertyTax: 0,
            monthlyHoaFee: 0,
            monthlyInsurance: 0,
            monthlyMaintenanceCost: 0,
            totalMonthlyPayment: 0,
            estimatedClosingCost: 0,
            totalOneTimePayment: 0,
            annualPropertyTax: 0,
            annualHoaFee: 0,
            annualInsurance: 0,
            annualMaintenanceCost: 0,
        }

        const monthlyRate = debouncedInterestRate / 100 / 12
        const numberOfPayments = debouncedLoanTerm * 12

        for (let iteration = 0; iteration < 100; iteration++) {
            const testHousePrice = (low + high) / 2

            // Calculate down payment
            const downPaymentAmount = downPaymentMode === "percentage"
                ? (testHousePrice * debouncedDownPayment) / 100
                : debouncedDownPayment

            // Calculate loan amount
            const loanAmount = Math.max(0, testHousePrice - downPaymentAmount)

            if (loanAmount <= 0) {
                high = testHousePrice
                continue
            }

            // Calculate monthly mortgage payment
            let monthlyMortgagePayment = 0
            if (monthlyRate === 0) {
                monthlyMortgagePayment = loanAmount / numberOfPayments
            } else {
                monthlyMortgagePayment =
                    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
                    (Math.pow(1 + monthlyRate, numberOfPayments) - 1)
            }

            // Calculate monthly property tax
            const monthlyPropertyTax = propertyTaxMode === "percentage"
                ? (testHousePrice * debouncedPropertyTax) / 100 / 12
                : debouncedPropertyTax / 12

            // Calculate monthly HOA fee
            const monthlyHoaFee = hoaFeeMode === "percentage"
                ? (testHousePrice * debouncedHoaFee) / 100 / 12
                : debouncedHoaFee / 12

            // Calculate monthly insurance
            const monthlyInsurance = insuranceMode === "percentage"
                ? (testHousePrice * debouncedInsurance) / 100 / 12
                : debouncedInsurance / 12

            // Calculate monthly maintenance cost
            const monthlyMaintenanceCost = maintenanceCostMode === "percentage"
                ? (testHousePrice * debouncedMaintenanceCost) / 100 / 12
                : debouncedMaintenanceCost / 12

            // Calculate total monthly payment
            // If includeTaxesAndFees is true, include property tax, HOA, insurance, and maintenance
            // Otherwise, only include mortgage payment
            let totalMonthlyPayment = monthlyMortgagePayment
            if (includeTaxesAndFees) {
                totalMonthlyPayment += monthlyPropertyTax + monthlyHoaFee + monthlyInsurance + monthlyMaintenanceCost
            }

            // Check if this fits within the budget
            if (totalMonthlyPayment <= debouncedMonthlyBudget && totalMonthlyPayment > 0) {
                // Calculate additional costs for this test price
                const testEstimatedClosingCost = testHousePrice * 0.03
                const testAnnualPropertyTax = monthlyPropertyTax * 12
                const testAnnualHoaFee = monthlyHoaFee * 12
                const testAnnualInsurance = monthlyInsurance * 12
                const testAnnualMaintenanceCost = monthlyMaintenanceCost * 12
                const testTotalOneTimePayment = downPaymentAmount + testEstimatedClosingCost

                bestResult = {
                    maxHousePrice: testHousePrice,
                    maxLoanAmount: loanAmount,
                    downPaymentAmount: downPaymentAmount,
                    monthlyMortgagePayment: monthlyMortgagePayment,
                    monthlyPropertyTax: monthlyPropertyTax,
                    monthlyHoaFee: monthlyHoaFee,
                    monthlyInsurance: monthlyInsurance,
                    monthlyMaintenanceCost: monthlyMaintenanceCost,
                    totalMonthlyPayment: totalMonthlyPayment,
                    estimatedClosingCost: testEstimatedClosingCost,
                    totalOneTimePayment: testTotalOneTimePayment,
                    annualPropertyTax: testAnnualPropertyTax,
                    annualHoaFee: testAnnualHoaFee,
                    annualInsurance: testAnnualInsurance,
                    annualMaintenanceCost: testAnnualMaintenanceCost,
                }
                low = testHousePrice
            } else {
                high = testHousePrice
            }

            // Break if we're close enough
            if (high - low < 1) {
                break
            }
        }

        return bestResult
    }, [
        debouncedMonthlyBudget,
        debouncedInterestRate,
        debouncedLoanTerm,
        debouncedDownPayment,
        debouncedPropertyTax,
        debouncedHoaFee,
        debouncedInsurance,
        debouncedMaintenanceCost,
        downPaymentMode,
        propertyTaxMode,
        hoaFeeMode,
        insuranceMode,
        maintenanceCostMode,
        includeTaxesAndFees,
    ])

    // Ref to store handleChange function for housePrice field
    const housePriceHandleChangeRef = useRef<((name: string, value: any) => void) | null>(null)

    // Helper function to update state from form data
    const updateStateFromFormData = (data: any) => {
        if (data.monthlyBudget !== undefined) setMonthlyBudget(Number(data.monthlyBudget))
        if (data.loanTerm !== undefined) setLoanTerm(Number(data.loanTerm))
        if (data.interestRate !== undefined) setInterestRate(Number(data.interestRate))
        if (data.downPayment !== undefined) setDownPayment(Number(data.downPayment))
        if (data.downPayment_mode !== undefined) setDownPaymentMode(data.downPayment_mode)
        if (data.includeTaxesAndFees !== undefined) setIncludeTaxesAndFees(data.includeTaxesAndFees)
        if (data.propertyTax !== undefined) setPropertyTax(Number(data.propertyTax))
        if (data.propertyTax_mode !== undefined) setPropertyTaxMode(data.propertyTax_mode)
        if (data.hoaFee !== undefined) setHoaFee(Number(data.hoaFee))
        if (data.hoaFee_mode !== undefined) setHoaFeeMode(data.hoaFee_mode)
        if (data.insurance !== undefined) setInsurance(Number(data.insurance))
        if (data.insurance_mode !== undefined) setInsuranceMode(data.insurance_mode)
        if (data.maintenanceCost !== undefined) setMaintenanceCost(Number(data.maintenanceCost))
        if (data.maintenanceCost_mode !== undefined) setMaintenanceCostMode(data.maintenanceCost_mode)
        // Note: housePrice is calculated and updated programmatically, not from form input
    }

    // Update housePrice in formData when calculation changes (without remounting)
    useEffect(() => {
        if (housePriceHandleChangeRef.current) {
            // Update housePrice field using handleChange - this updates formData without remounting
            housePriceHandleChangeRef.current('housePrice', affordabilityCalculation.maxHousePrice)
        }
    }, [affordabilityCalculation.maxHousePrice])

    const steps: Step[] = [
        {
            id: "budget-and-loan",
            title: "Budget & Loan Details",
            description: "Enter your monthly budget and mortgage loan information",
            fields: [
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "monthlyBudget",
                    label: "Budget for house",
                    placeholder: "3500",
                    required: true,
                    minValue: 0,
                    maxValue: 10000000,
                    allowNegative: false,
                    incrementValues: [100, 500, 1000],
                    ui: { colSpan: 12 },
                    description: "per month",
                    laravelValidationRules: {
                        monthlyBudget: 'required|numeric|min:0'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "loanTerm",
                    label: "Mortgage loan term",
                    placeholder: "30",
                    required: true,
                    minValue: 1,
                    maxValue: 50,
                    allowNegative: false,
                    incrementValues: [1, 5],
                    ui: { colSpan: 12 },
                    description: "years",
                    laravelValidationRules: {
                        loanTerm: 'required|integer|min:1|max:50'
                    }
                },
                {
                    type: "numberInputWithCommasPlusMinusCompact",
                    name: "interestRate",
                    label: "Interest rate",
                    placeholder: "6.317",
                    required: true,
                    minValue: 0,
                    maxValue: 100,
                    allowNegative: false,
                    incrementValues: [1, 0.1, 0.01],
                    ui: { colSpan: 12 },
                    description: "%",
                    laravelValidationRules: {
                        interestRate: 'required|numeric|min:0|max:100'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "downPayment",
                    label: "Down payment",
                    placeholder: "20",
                    required: true,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "%",
                    laravelValidationRules: {
                        downPayment: 'required|numeric|min:0|max:100'
                    }
                },
            ]
        },
        {
            id: "taxes-and-fees",
            title: "Taxes and Fees",
            description: "Enter annual costs as percentage or dollar amount",
            fields: [
                {
                    type: "checkbox",
                    name: "includeTaxesAndFees",
                    label: "Include the tax and fees below into the budget",
                    required: false,
                    // ui: { colSpan: 12 },
                    laravelValidationRules: {
                        includeTaxesAndFees: 'nullable|boolean'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "propertyTax",
                    label: "Property tax",
                    placeholder: "1.5",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    visibleWhen: [
                        { field: "includeTaxesAndFees", op: "=" as const, value: true }
                    ],
                    laravelValidationRules: {
                        propertyTax: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "hoaFee",
                    label: "HOA or co-op fee",
                    placeholder: "0",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    visibleWhen: [
                        { field: "includeTaxesAndFees", op: "=" as const, value: true }
                    ],
                    laravelValidationRules: {
                        hoaFee: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "insurance",
                    label: "Insurance",
                    placeholder: "0.5",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    visibleWhen: [
                        { field: "includeTaxesAndFees", op: "=" as const, value: true }
                    ],
                    laravelValidationRules: {
                        insurance: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "numberInputPercentageValue",
                    name: "maintenanceCost",
                    label: "Maintenance cost (repair, utility etc.)",
                    placeholder: "1.5",
                    required: false,
                    referenceField: "housePrice",
                    ui: { colSpan: 12 },
                    description: "per year",
                    visibleWhen: [
                        { field: "includeTaxesAndFees", op: "=" as const, value: true }
                    ],
                    laravelValidationRules: {
                        maintenanceCost: 'nullable|numeric|min:0'
                    }
                },
                {
                    type: "reactNode",
                    name: "housePrice",
                    label: "House Price (calculated)",
                    reactNode: null, // Will be set by custom renderer
                    ui: { colSpan: 12 },
                    hidden: true,
                    // Custom renderer that auto-updates housePrice
                    render: ({ handleChange, formData }: { handleChange: (name: string, value: any) => void; formData: Record<string, any> }) => {
                        // Store handleChange so we can use it to update housePrice
                        housePriceHandleChangeRef.current = handleChange

                        // Update housePrice if it's different from calculated value
                        const currentHousePrice = formData?.housePrice || 0
                        const calculatedHousePrice = affordabilityCalculation.maxHousePrice
                        if (Math.abs(currentHousePrice - calculatedHousePrice) > 0.01) {
                            // Use setTimeout to avoid updating during render
                            setTimeout(() => {
                                if (housePriceHandleChangeRef.current) {
                                    housePriceHandleChangeRef.current('housePrice', calculatedHousePrice)
                                }
                            }, 0)
                        }

                        return null // Hidden field, no UI
                    }
                },
            ]
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
        >
            <div>
                <h1 className="text-3xl font-bold mb-2">Month Budget Based House Affordability</h1>
                <p className="text-muted-foreground">
                    Calculate how much house you can afford based on your fixed monthly budget
                </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
                {/* Left Side - Form */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-2 h-full"
                >
                    <JsonFormSteps
                        divClassName="w-full p-0 h-full"
                        hideSubmitButton={true}
                        steps={steps}
                        onSubmit={(data: any) => {
                            updateStateFromFormData(data)
                        }}
                        onChange={useMemo(
                            () =>
                                debounce((data: any) => {
                                    updateStateFromFormData(data)
                                }, 500),
                            []
                        )}
                        debug={false}
                        debugSingleForm={true}
                        progressBarPosition="top"
                        noCard={false}
                        horizontalLayout={true}
                        initialValues={{
                            monthlyBudget: monthlyBudget,
                            loanTerm: loanTerm,
                            interestRate: interestRate,
                            downPayment: downPayment,
                            downPayment_mode: downPaymentMode,
                            includeTaxesAndFees: includeTaxesAndFees,
                            propertyTax: propertyTax,
                            propertyTax_mode: propertyTaxMode,
                            hoaFee: hoaFee,
                            hoaFee_mode: hoaFeeMode,
                            insurance: insurance,
                            insurance_mode: insuranceMode,
                            maintenanceCost: maintenanceCost,
                            maintenanceCost_mode: maintenanceCostMode,
                            housePrice: affordabilityCalculation.maxHousePrice, // Add housePrice to formData for reference
                        }}
                        resetOnInitialValuesChange={false}
                    />
                </motion.div>

                {/* Right Side - Results */}
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                    className="lg:col-span-3 xl:col-span-1 space-y-6"
                >
                    {/* Summary */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <p className="text-base leading-relaxed">
                            With a monthly budget of <span className="font-bold text-primary text-lg">$<AnimatedNumber value={debouncedMonthlyBudget} decimals={0} /></span>, you can afford a house up to <span className="font-bold text-primary text-lg">$<AnimatedNumber value={affordabilityCalculation.maxHousePrice} decimals={0} /></span>, within which <span className="font-bold">$<AnimatedNumber value={affordabilityCalculation.maxLoanAmount} decimals={0} /></span> is the loan and <span className="font-bold">$<AnimatedNumber value={affordabilityCalculation.downPaymentAmount} decimals={0} /></span> is the down payment.
                        </p>
                    </div>

                    {/* Borrowing and One-Time Payments */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Borrowing and One-Time Payments</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">You can borrow:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.maxLoanAmount} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Total price of the house:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.maxHousePrice} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Down payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.downPaymentAmount} decimals={0} />
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Estimated closing cost (one time, assume 3%):</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.estimatedClosingCost} decimals={0} />
                                </span>
                            </div>
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Total one-time payment at closing:</span>
                                <span className="text-base font-bold text-primary">
                                    $<AnimatedNumber value={affordabilityCalculation.totalOneTimePayment} decimals={0} />
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Monthly and Annual Costs */}
                    <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">Monthly and Annual Costs</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground">Monthly mortgage payment:</span>
                                <span className="text-sm font-semibold">
                                    $<AnimatedNumber value={affordabilityCalculation.monthlyMortgagePayment} decimals={0} />
                                </span>
                            </div>
                            {includeTaxesAndFees && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Monthly property tax:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={affordabilityCalculation.monthlyPropertyTax} decimals={0} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Monthly HOA or co-op fee:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={affordabilityCalculation.monthlyHoaFee} decimals={0} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Monthly insurance:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={affordabilityCalculation.monthlyInsurance} decimals={0} />
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-muted-foreground">Monthly maintenance cost:</span>
                                        <span className="text-sm font-semibold">
                                            $<AnimatedNumber value={affordabilityCalculation.monthlyMaintenanceCost} decimals={0} />
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="pt-3 border-t border-border flex justify-between">
                                <span className="text-base font-semibold">Total monthly cost:</span>
                                <span className="text-base font-bold text-primary">
                                    $<AnimatedNumber value={affordabilityCalculation.totalMonthlyPayment} decimals={0} />
                                </span>
                            </div>
                            {includeTaxesAndFees && (
                                <>
                                    <div className="pt-3 border-t border-border space-y-2">
                                        <div className="text-sm font-semibold mb-2">Annual Costs:</div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">Annual property tax:</span>
                                            <span className="text-sm font-semibold">
                                                $<AnimatedNumber value={affordabilityCalculation.annualPropertyTax} decimals={0} />
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">Annual HOA or co-op fee:</span>
                                            <span className="text-sm font-semibold">
                                                $<AnimatedNumber value={affordabilityCalculation.annualHoaFee} decimals={0} />
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">Annual insurance:</span>
                                            <span className="text-sm font-semibold">
                                                $<AnimatedNumber value={affordabilityCalculation.annualInsurance} decimals={0} />
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-muted-foreground">Annual maintenance cost:</span>
                                            <span className="text-sm font-semibold">
                                                $<AnimatedNumber value={affordabilityCalculation.annualMaintenanceCost} decimals={0} />
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    )
}

